import { pxToRem } from '@/Client/Styles/pxToRem';
import { css } from '@emotion/react';
import { kaiPartialIdPresenter } from '@kaiizen/kai-presenters';
import { useScanEditingContextSlice, useTaskModifierDisplayNames } from '@kaiizen/kai-pro-api-react-state';
import {
  buildLineItemKey,
  ChangeType,
  formatCurrency,
  formatWorkStatus,
  getChangeTypeLabel,
  REACT_PRINT_STYLES,
  ScopeReportLineItem,
  truncateNotesText,
} from '@kaiizen/kai-reports';
import WarningIcon from '@mui/icons-material/Warning';
import { useMemo, useState } from 'react';
import { ChangeOrderIcon, EditIcon } from '../../Icons';
import { COLORS2, TYPOGRAPHY2 } from '../../Styles';
import { WorkStatusIcon } from '../ScanEditor/ProgressWalk/WorkStatusIcons';
import { LineItemInlineActionDropDown } from './LineItemInlineActionDropDown';
import { useScopePrintPreviewContext } from './ScopePrintPreviewContext';

const CLICKABLE_ROW_STYLE = css`
  cursor: pointer;

  @media print {
    cursor: default;
  }
`;

const PRODUCT_OUT_OF_STOCK_CELL_STYLE = css`
  background-color: ${COLORS2.alwaysDark.offWhiteRed};
`;

const HOVERED_ROW_STYLE = css`
  background-color: ${COLORS2.alwaysDark.offWhite} !important;

  @media print {
    background-color: transparent !important;
  }
`;

const HOVERED_BANNER_TD_STYLE = css`
  background-color: ${COLORS2.alwaysDark.offWhite} !important;

  @media print {
    background-color: transparent !important;
  }
`;

const SELECTED_ROW_STYLE = css`
  background-color: ${COLORS2.alwaysDark.lightGray} !important;
  position: relative;


  @media print {
    background-color: transparent !important;
    box-shadow: none !important;
  }
`;

const SELECTED_ROW_TOP_STYLE = css`
  background-color: ${COLORS2.alwaysDark.lightGray} !important;
  position: relative;


  @media print {
    background-color: transparent !important;
    box-shadow: none !important;
  }
`;

const SELECTED_ROW_MIDDLE_STYLE = css`
  background-color: ${COLORS2.alwaysDark.lightGray} !important;
  position: relative;


  @media print {
    background-color: transparent !important;
    box-shadow: none !important;
  }
`;

const SELECTED_ROW_BOTTOM_STYLE = css`
  background-color: ${COLORS2.alwaysDark.lightGray} !important;
  position: relative;
  @media print {
    background-color: transparent !important;
    box-shadow: none !important;
  }
`;

const STRIKETHROUGH_STYLE = css`
  text-decoration: line-through;
  color: ${COLORS2.alwaysDark.darkGray};
  margin-right: 8px;
`;

const CHANGED_CELL_HIGHLIGHT_STYLE = css`
  background-color: ${COLORS2.alwaysDark.lightOrange} !important;
`;

const UNASSIGNED_CONTRACTOR_CELL_STYLE = css`
  background-color: ${COLORS2.alwaysDark.lighterRed} !important;
  color: ${COLORS2.alwaysDark.black} !important;
  font-weight: 400;
`;

const CHANGE_TYPE_BADGE_STYLE = css`
  display: inline-block;
  border-radius: 4px;
  color: ${COLORS2.alwaysDark.black};
  padding-top: 3px;
`;

const TASK_NAME_STYLE = css`
  padding-top: 3px;
`;

const WARNING_ICON_STYLE = css`
  color: ${COLORS2.alwaysDark.lightOrange};
  font-size: ${pxToRem(20)};
`;

const ROW_BANNER_TD_BASE_STYLE = css`
  padding: 6px 12px;
  ${TYPOGRAPHY2.xSmallBold}
  color: ${COLORS2.alwaysDark.black};
  border-bottom: solid 1px ${COLORS2.alwaysDark.lightGray};
`;

const ROW_BANNER_TD_ATTENTION_SUMMARY_STYLE = css`
  ${ROW_BANNER_TD_BASE_STYLE}
  background-color: ${COLORS2.alwaysDark.lightOrange};
`;

const ROW_BANNER_TD_TRADE_PAGE_STYLE = css`
  ${ROW_BANNER_TD_BASE_STYLE}
  background-color: ${COLORS2.alwaysDark.white};
  border: solid 1px ${COLORS2.alwaysDark.lightGray};
  padding: 10px 10px 10px 10px;
`;

const ROW_BANNER_TD_TRADE_PAGE_FOOTER_STYLE = css`
  ${ROW_BANNER_TD_BASE_STYLE}
  background-color: ${COLORS2.alwaysDark.white};
  border: solid 1px ${COLORS2.alwaysDark.lightGray};
  padding: 10px 10px 10px 10px;
`;

const ROW_BANNER_CONTENT_WRAPPER_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ROW_BANNER_HEADER_ROW_STYLE = css`
  display: flex;
  align-items: top;
  justify-content: space-between;
  gap: 8px;
`;

const ROW_BANNER_NOTE_STYLE = css`
  font-weight: normal;
  white-space: normal;
`;

const ROW_BANNER_TD_PUNCHLIST_STYLE = css`
  background-color: rgba(173, 216, 230, 0.3);
`;

const ROW_BANNER_TD_CLOSEOUT_STYLE = css`
  ${ROW_BANNER_TD_BASE_STYLE}
  background-color: ${COLORS2.alwaysDark.offWhiteGreen};
`;

const ROW_BANNER_TD_EDIT_REQUEST_STYLE = css`
  ${ROW_BANNER_TD_BASE_STYLE}
  background-color: ${COLORS2.alwaysDark.lightYellow};
`;

const ROW_BANNER_TASK_ID_STYLE = css`
  white-space: nowrap;
  margin-left: auto;
`;

const ROW_BANNER_WARNING_ICON_STYLE = css`
  color: ${COLORS2.alwaysDark.orange};
  ${TYPOGRAPHY2.medium}
`;

const ROW_BANNER_TASK_DESCRIPTION_STYLE = css`
  ${TYPOGRAPHY2.xSmall}
  color: ${COLORS2.alwaysDark.text.darkGray};
`;

const WORK_STATUS_CELL_STYLE = css`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/**
 * Baseline vs current diff for change-order report rows.
 *
 * `changedColumns` names are internal change-order column ids (`deferred` / `tenantResponsibility`
 * match the two built-in modifier tags; custom org modifiers have no slot). They select highlighted
 * cells and the modifier names listed in the change-type label (`getChangeTypeLabel`, which prints
 * the org's display names); the ids themselves are never printed.
 */
interface ChangeOrderData {
  baselineContractorName?: string;
  baselineQuantity?: number;
  baselineUnitPrice?: number;
  baselineTotalPrice?: number;
  changedColumns: {
    contractor: boolean;
    quantity: boolean;
    rate: boolean;
    amount: boolean;
    deferred: boolean;
    tenantResponsibility: boolean;
  };
  changeType?: 'existing' | 'new' | 'deleted' | 'reversal' | 'restatement' | 'modified';
}

/**
 * One scope-report line item row (web print preview).
 *
 * Task-modifier labels shown in the task banner come from org settings via
 * `useTaskModifierDisplayNames()` (`modifierLabels.*`). Identifiers such as `isDeferredLineItem`,
 * `isTenantRespLineItem`, and `changedColumns.deferred` are logic/diff flags only.
 */
export const ScopeLineItemRow = ({
  item,
  lastItem,
  isSelected,
  lineItemId,
  changeOrderData,
  showRowBanner = false,
  taskAnchorId,
}: {
  item: ScopeReportLineItem;
  lastItem?: ScopeReportLineItem;
  isSelected?: boolean;
  lineItemId?: string;
  changeOrderData?: ChangeOrderData;
  showRowBanner?: boolean;
  taskAnchorId?: string;
}) => {
  const { scanScopeState } = useScanEditingContextSlice(['scanScopeState']);
  const modifierLabels = useTaskModifierDisplayNames();
  const { onLineItemClick: onRowClick, reportType, currentDropdown, setCurrentDropdown, changeOrderLookup } = useScopePrintPreviewContext();
  const [isHovered, setIsHovered] = useState(false);
  const isActionDropdownOpen = currentDropdown?.groupId === item.groupId && currentDropdown?.taskId === item.taskId;
  const isPendingScope = scanScopeState === 'draft';
  const isApprovedChangeOrderScope = scanScopeState === 'current' || scanScopeState === 'outdated';
  const isHistoricalScope = scanScopeState === 'outdated';
  const isFullScopeReport = reportType === 'fullScope';
  const isChangeOrderReport = reportType === 'changeOrder';
  const isPunchlistReport = reportType === 'punchlist';
  const isCloseoutReport = reportType === 'closeout';

  const changeOrderType = useMemo<ChangeType | null>(() => {
    if (changeOrderData?.changeType) return changeOrderData.changeType;
    if (!changeOrderLookup) return null;
    const key = buildLineItemKey(item.groupId, item.taskId, item.optionId, item.lineItemId);
    return changeOrderLookup.get(key) || null;
  }, [changeOrderData, changeOrderLookup, item.groupId, item.taskId, item.optionId, item.lineItemId]);

  // UI flag: should we show change order diff styling/presentation?
  const isChangeOrderDiff = !!changeOrderData;

  // Data flag: is this item actually part of a change order?
  const isChangeOrderItem = !!changeOrderType;
  const isEditRequest = item.isEditRequest === true && item.isEditResolution === false;
  const isEditResolution = item.isEditResolution === true;
  const isAdminEditResolution = item.isAdminEditResolution === true;
  const isDeferredLineItem = item.isDeferredLineItem === true;
  const isTenantRespLineItem = item.isTenantRespLineItem === true;
  const hasTaskNotes = item.taskNotes && item.taskNotes.length > 0 && item.taskNotes.join('') !== '';

  const handleRowClick = () => {
    if (!onRowClick || !lineItemId) return;
    setCurrentDropdown(
      isActionDropdownOpen ? undefined : { groupId: item.groupId, taskId: item.taskId },
    );
  };

  const renderRowHeaderNote = () => {
    if (!showRowBanner) return null;

    const getBannerTdStyle = () => {
      if (isEditRequest && !isHistoricalScope) return ROW_BANNER_TD_EDIT_REQUEST_STYLE;
      if (isChangeOrderItem && !isHistoricalScope) return ROW_BANNER_TD_ATTENTION_SUMMARY_STYLE;
      if (isChangeOrderReport && !isHistoricalScope) return ROW_BANNER_TD_ATTENTION_SUMMARY_STYLE;
      if (isPunchlistReport && !isHistoricalScope) return ROW_BANNER_TD_PUNCHLIST_STYLE;
      if (isCloseoutReport && !isHistoricalScope) return ROW_BANNER_TD_CLOSEOUT_STYLE;
      return ROW_BANNER_TD_TRADE_PAGE_STYLE;
    };

    return (
      <tr
        id={taskAnchorId}
        css={[
          onRowClick && CLICKABLE_ROW_STYLE,
          isSelected && SELECTED_ROW_TOP_STYLE,
        ]}
        onMouseEnter={onRowClick ? () => setIsHovered(true) : undefined}
        onMouseLeave={onRowClick ? () => setIsHovered(false) : undefined}
        onClick={onRowClick && lineItemId ? handleRowClick : undefined}
      >
        <td
          colSpan={7}
          css={[
            getBannerTdStyle(),
            onRowClick && isHovered && HOVERED_BANNER_TD_STYLE,
          ]}
        >
          <div css={ROW_BANNER_CONTENT_WRAPPER_STYLE}>
            <div css={ROW_BANNER_HEADER_ROW_STYLE}>
              {isFullScopeReport && !isAdminEditResolution && (isEditRequest || isEditResolution) && (
                <EditIcon size={20} color={COLORS2.alwaysDark.darkYellow} />
              )}
              {isFullScopeReport && isChangeOrderItem && <ChangeOrderIcon size={20} color={COLORS2.alwaysDark.black} />}
              {isChangeOrderDiff && <WarningIcon css={ROW_BANNER_WARNING_ICON_STYLE} />}
              {isChangeOrderDiff && changeOrderData && (
                <span css={CHANGE_TYPE_BADGE_STYLE}>
                  {getChangeTypeLabel(changeOrderData, modifierLabels)}
                </span>
              )}
              <span css={TASK_NAME_STYLE}>
                {item.taskName}
                {isFullScopeReport && isChangeOrderItem && (
                  <>(Change Order {isPendingScope ? '- Pending Review' : isApprovedChangeOrderScope ? '- Approved' : ''})</>
                )}
                {isFullScopeReport && isEditRequest && !isAdminEditResolution && <>(Edit Request - Pending Review)</>}
                {isFullScopeReport && isEditResolution && !isAdminEditResolution && <>(Edit Request - Resolved)</>}
                {isFullScopeReport && isDeferredLineItem && <>({modifierLabels.deferred} Task)</>}
                {isFullScopeReport && isTenantRespLineItem && <>({modifierLabels.tenantResponsibility})</>}
              </span>
              {item.taskDescription && <span css={ROW_BANNER_TASK_DESCRIPTION_STYLE}>{item.taskDescription}</span>}
              <span css={ROW_BANNER_TASK_ID_STYLE} data-full-task-id={item.taskId} title={item.taskId || undefined}>
                Task ID: {item.taskId ? kaiPartialIdPresenter(item.taskId) : '--'}
              </span>
            </div>
            {hasTaskNotes && (
              <div css={ROW_BANNER_NOTE_STYLE}>
                <strong>{item.isPunchlistTask ? 'Note: ' : 'Task Note: '}</strong>
                {truncateNotesText(item.taskNotes!)}
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderRowFooterNote = () => {
    if (!showRowBanner) return null;
    if (!item.lineItemNotes || item.lineItemNotes.length === 0) return null;

    const lineItemNotes = item.lineItemNotes;

    return (
      <tr
        css={[
          onRowClick && CLICKABLE_ROW_STYLE,
          isSelected && SELECTED_ROW_BOTTOM_STYLE,
        ]}
        onMouseEnter={onRowClick ? () => setIsHovered(true) : undefined}
        onMouseLeave={onRowClick ? () => setIsHovered(false) : undefined}
        onClick={onRowClick && lineItemId ? handleRowClick : undefined}
      >
        <td
          colSpan={7}
          css={[
            ROW_BANNER_TD_TRADE_PAGE_FOOTER_STYLE,
            onRowClick && isHovered && HOVERED_BANNER_TD_STYLE,
          ]}
        >
          <div css={ROW_BANNER_CONTENT_WRAPPER_STYLE}>
            <div css={ROW_BANNER_NOTE_STYLE}>
              <strong>Line Item Note:</strong>
              {truncateNotesText(lineItemNotes)}
            </div>
            <span css={ROW_BANNER_TASK_ID_STYLE} data-full-task-id={item.taskId}></span>
          </div>
        </td>
      </tr>
    );
  };

  const renderRowMetadata = () => {
    return (
      <tr
        css={[
          onRowClick && CLICKABLE_ROW_STYLE,
          onRowClick && isHovered && HOVERED_ROW_STYLE,
          isSelected && (showRowBanner ? SELECTED_ROW_MIDDLE_STYLE : SELECTED_ROW_STYLE),
        ]}
        onMouseEnter={onRowClick ? () => setIsHovered(true) : undefined}
        onMouseLeave={onRowClick ? () => setIsHovered(false) : undefined}
        onClick={onRowClick && lineItemId ? handleRowClick : undefined}
      >
        {renderWorkStatusCell()}
        {renderContractorCell()}
        {renderTaskDescriptionCell()}
        <td css={[REACT_PRINT_STYLES.td, item.productExists === false && PRODUCT_OUT_OF_STOCK_CELL_STYLE]}>
          {item.productName || '-'}
          {item.productSku && (
            <>
              <br />
              sku: {item.productSku}
            </>
          )}
          {item.productBrand && (
            <>
              <br />
              brand: {item.productBrand}
            </>
          )}
        </td>
        {renderQuantityCell()}
        {renderRateCell()}
        {renderAmountCell()}
      </tr>
    );
  };

  const renderWorkStatusCell = () => {
    return (
      <td css={REACT_PRINT_STYLES.td}>
        <div css={WORK_STATUS_CELL_STYLE}>
          {item.taskWorkStatus && <WorkStatusIcon status={item.taskWorkStatus} black />}
          {formatWorkStatus(item.taskWorkStatus)}
        </div>
      </td>
    );
  };

  const renderContractorCell = () => {
    const isUnassigned = item.contractorName?.toLowerCase() === 'unassigned';

    if (changeOrderData) {
      return (
        <td
          css={[
            REACT_PRINT_STYLES.td,
            changeOrderData.changedColumns.contractor && !isHistoricalScope && CHANGED_CELL_HIGHLIGHT_STYLE,
            isUnassigned && UNASSIGNED_CONTRACTOR_CELL_STYLE,
          ]}
        >
          {changeOrderData.baselineContractorName && changeOrderData.changedColumns.contractor && (
            <>
              <span css={STRIKETHROUGH_STYLE}>{changeOrderData.baselineContractorName}</span>
              <br />
            </>
          )}
          {item.contractorName || ''}
        </td>
      );
    }
    return (
      <td
        css={[
          REACT_PRINT_STYLES.td,
          isUnassigned && UNASSIGNED_CONTRACTOR_CELL_STYLE,
        ]}
      >
        {item.contractorName || ''}
      </td>
    );
  };

  const renderTaskDescriptionCell = () => {
    return <td css={REACT_PRINT_STYLES.td}>{item.scopePDFTaskDescName || ''}</td>;
  };

  const renderQuantityCell = () => {
    if (changeOrderData) {
      return (
        <td
          css={[
            REACT_PRINT_STYLES.td,
            REACT_PRINT_STYLES.nowrap,
            changeOrderData.changedColumns.quantity && !isHistoricalScope && CHANGED_CELL_HIGHLIGHT_STYLE,
          ]}
        >
          {changeOrderData.baselineQuantity != null && changeOrderData.changedColumns.quantity && (
            <>
              <span css={STRIKETHROUGH_STYLE}>{changeOrderData.baselineQuantity}</span>
              <br />
            </>
          )}
          {item.productQuantity || '-'}
        </td>
      );
    }
    return <td css={[REACT_PRINT_STYLES.td, REACT_PRINT_STYLES.nowrap]}>{item.productQuantity || '-'}</td>;
  };

  const renderRateCell = () => {
    const unitPrice = item.unitPrice || 0;

    const quantityValueType = item.productQuantityValueType && item.productQuantityValueType !== 'count'
      ? ` ${item.productQuantityValueType}`
      : ' each';
    const rateText = `${formatCurrency(unitPrice)}${quantityValueType}`;

    if (changeOrderData) {
      const baselineRateText = changeOrderData.baselineUnitPrice != null
        ? `${formatCurrency(changeOrderData.baselineUnitPrice)}${quantityValueType}`
        : '';

      return (
        <td
          css={[
            REACT_PRINT_STYLES.td,
            REACT_PRINT_STYLES.nowrap,
            changeOrderData.changedColumns.rate && !isHistoricalScope && CHANGED_CELL_HIGHLIGHT_STYLE,
          ]}
        >
          {changeOrderData.baselineUnitPrice != null && changeOrderData.changedColumns.rate && (
            <>
              <span css={STRIKETHROUGH_STYLE}>{baselineRateText}</span>
              <br />
            </>
          )}
          {rateText}
        </td>
      );
    }
    return (
      <td css={[REACT_PRINT_STYLES.td, REACT_PRINT_STYLES.nowrap]}>
        {rateText}
      </td>
    );
  };

  const renderAmountCell = () => {
    const totalPrice = item.totalLineItemCost || 0;

    if (changeOrderData) {
      return (
        <td
          css={[
            REACT_PRINT_STYLES.td,
            REACT_PRINT_STYLES.nowrap,
            changeOrderData.changedColumns.amount && !isHistoricalScope && CHANGED_CELL_HIGHLIGHT_STYLE,
          ]}
        >
          {changeOrderData.baselineTotalPrice != null && changeOrderData.changedColumns.amount && (
            <>
              <span css={STRIKETHROUGH_STYLE}>
                {formatCurrency(changeOrderData.baselineTotalPrice)}
              </span>
              <br />
            </>
          )}
          {formatCurrency(totalPrice)}
        </td>
      );
    }
    return <td css={[REACT_PRINT_STYLES.td, REACT_PRINT_STYLES.nowrap]}>{formatCurrency(totalPrice)}</td>;
  };

  const lastItemIsSameTask = lastItem
    && item.taskId === lastItem.taskId
    && item.groupId === lastItem.groupId
    && !isChangeOrderItem
    && !isEditRequest
    && !isEditResolution
    && !isDeferredLineItem
    && !isTenantRespLineItem;

  if (item.isStatusTask === true) {
    return (
      <>
        {lastItemIsSameTask ? null : renderRowHeaderNote()}
        <tr>
          <td css={REACT_PRINT_STYLES.td}>
            <div css={WORK_STATUS_CELL_STYLE}>
              {item.taskWorkStatus && <WorkStatusIcon status={item.taskWorkStatus} black />}
              {formatWorkStatus(item.taskWorkStatus)}
            </div>
          </td>
          <td css={REACT_PRINT_STYLES.td}>{item.contractorName || ''}</td>
          <td css={REACT_PRINT_STYLES.td}>{item.optionName || ''}</td>
          <td css={REACT_PRINT_STYLES.td}>Status: {item.statusLabel || item.statusValue || '-'}</td>
          {renderQuantityCell()}
          {/* NOTE: this is a hack to keep the UI/UX backward compatible with the old definition of status tasks*/}
          {item.unitPrice === 0
            ? <td css={REACT_PRINT_STYLES.td}>-</td>
            : renderRateCell()}
          {item.totalLineItemCost === 0
            ? <td css={REACT_PRINT_STYLES.td}>-</td>
            : renderAmountCell()}
        </tr>
        <LineItemInlineActionDropDown
          item={item}
        />
        {renderRowFooterNote()}
      </>
    );
  }

  return (
    <>
      {lastItemIsSameTask ? null : renderRowHeaderNote()}
      {renderRowMetadata()}
      <LineItemInlineActionDropDown
        item={item}
      />
      {renderRowFooterNote()}
    </>
  );
};
